import './PromoAccessories.scss';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GatsbyImageWrapper from '../GatsbyImageWrapper';
import rightArrowIcon from '../../images/englander-icons/Right-Arrow-Icon.svg';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { OrdersService } from '@progymedia/gatsby-theme-boutik-ecommerce/src/interfaces/services/ordersService';
import { OtherBoutikServicesContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/otherBoutikServices/otherBoutikServicesContext';
import { BoutikEC2ProxyRepository } from '@progymedia/gatsby-theme-boutik-ecommerce/src/interfaces/repositories/boutikEC2ProxyRepository';

export default function PromoAccessories(props: {
    promoCMS: any;
    allProducts: any;
}) {
    const { currentLocale, navigateTranslated } = usePageLocalization();

    const servicesContext = useContext(OtherBoutikServicesContext);
    const boutikEC2Service = servicesContext[
        'BoutikEC2ProxyRepository'
    ] as BoutikEC2ProxyRepository;

    const { t } = useTranslation('pages');
    const [reclaimableProductImage, setReclaimableProductImage] =
        useState<string>();
    const { promoCMS, allProducts } = props;
    const productWithPromo = allProducts?.find(
        (prod: any) =>
            prod.code ===
            promoCMS?.attributes?.product_code?.values?.[0]?.[currentLocale]
    );

    const link = `/claim-promotion/?id=${promoCMS?.id}`;

    useEffect(() => {
        if (promoCMS.attributes.claimable_product) {
            boutikEC2Service
                .getProductImage({
                    sku: promoCMS.attributes.claimable_product.values[0][
                        currentLocale
                    ],
                })
                .then((image) => {
                    setReclaimableProductImage(image);
                });
        }
    }, []);

    const removeFirstPathname = (slug: string) => {
        return slug.split('/').slice(2).join('/') + '/';
    };

    const currentDate = new Date();

    const stringDateToDate = (dateString: string) => {
        const [day, month, year] = dateString.split('/');
        return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const isPastDate = (endDateString: string, currentDate: Date) => {
        const endDate = stringDateToDate(endDateString);
        const currentDateOnly = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate()
        );
        return endDate < currentDateOnly;
    };

    const monthsInEnglish = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const monthsInFrench = [
        'Janvier',
        'Février',
        'Mars',
        'Avril',
        'Mai',
        'Juin',
        'Juillet',
        'Août',
        'Septembre',
        'Octobre',
        'Novembre',
        'Décembre',
    ];

    const formatDate = (from: string, to: string) => {
        const fromDateSegment = from.split('/');
        const toDateSegment = to.split('/');

        let fromDay = fromDateSegment[0];
        let toDay = toDateSegment[0];

        const month =
            currentLocale == 'fr_CA'
                ? monthsInFrench[Number(fromDateSegment[1]) - 1]
                : monthsInEnglish[Number(fromDateSegment[1]) - 1];

        const year = fromDateSegment[2];

        if (fromDay.length == 2 && fromDay[0] == '0') {
            fromDay = fromDay.slice(1);
        }

        if (toDay.length == 2 && toDay[0] == '0') {
            toDay = toDay.slice(1);
        }

        return t('promotions.from-to', {
            from: fromDay,
            to: toDay,
            month: month,
            year: year,
        });
    };

    const formatExpiredDate = (date: string) => {
        const dateSegment = date.split('/');

        const day = dateSegment[0];

        const month =
            currentLocale == 'fr_CA'
                ? monthsInFrench[Number(dateSegment[1]) - 1]
                : monthsInEnglish[Number(dateSegment[1]) - 1];

        const year = dateSegment[2];

        return currentLocale == 'fr_CA'
            ? `${day} ${month} ${year}`
            : `${month} ${day} ${year}`;
    };

    return (
        <div className='promo-accessories-container'>
            <div
                className='promo-image'
                onClick={() =>
                    navigateTranslated(
                        removeFirstPathname(
                            productWithPromo?.slug?.[currentLocale]
                        )
                    )
                }
            >
                <GatsbyImageWrapper fields={productWithPromo?.fields} />
                <div
                    className={`claimable-product-container-mobile ${reclaimableProductImage ? 'visible' : ''}`}
                >
                    <img
                        className='claimable-product-img'
                        src={reclaimableProductImage}
                    />
                    {reclaimableProductImage && (
                        <p className='claimable-product-sku'>
                            {
                                promoCMS.attributes?.claimable_product
                                    ?.values[0][currentLocale]
                            }
                        </p>
                    )}
                </div>
            </div>
            <div className='text-content'>
                <div>
                    <span className='date'>
                        {formatDate(
                            promoCMS?.attributes?.start_date?.values?.[0]?.[
                                currentLocale
                            ],
                            promoCMS?.attributes?.end_date?.values?.[0]?.[
                                currentLocale
                            ]
                        )}
                    </span>
                    {isPastDate(
                        promoCMS.attributes?.end_date?.values[0][currentLocale],
                        currentDate                        
                    ) && (
                        <div className='expiration-banner'>
                            {t('promotions.promotion-expired', {
                                date: formatExpiredDate(
                                    promoCMS.attributes.expiration_date
                                        ?.values[0][currentLocale]
                                ),
                            })}
                        </div>
                    )}
                    <div className='content-title'>
                        {promoCMS?.title?.[currentLocale]}
                    </div>
                    {promoCMS?.description && (
                        <div
                            className='content-desc'
                            dangerouslySetInnerHTML={{
                                __html: promoCMS?.description
                                    ?.valuesMarkdownRemark?.[currentLocale],
                            }}
                        />
                    )}
                </div>
                <div>
                    <button
                        className='promo-button'
                        onClick={() => navigateTranslated(link)}
                    >
                        {t('promotions.claim-promotion-btn')}
                        <img src={rightArrowIcon} alt=''></img>
                    </button>
                    {promoCMS.attributes?.promotion_condition?.values.map(
                        (condition) => {
                            return (
                                <p className='promotion-conditions'>
                                    {condition[currentLocale]}
                                </p>
                            );
                        }
                    )}
                </div>
            </div>
            <div
                className={`claimable-product-container-desktop ${reclaimableProductImage ? 'visible' : ''}`}
            >
                <>
                    <img
                        className='claimable-product-img'
                        src={reclaimableProductImage}
                    />
                    <p className='claimable-product-sku'>
                        {
                            promoCMS.attributes?.claimable_product?.values[0][
                                currentLocale
                            ]
                        }
                    </p>
                </>
            </div>
        </div>
    );
}

function ListDiv(props: { item: any; currentLocale: string }) {
    const { item, currentLocale } = props;
    return (
        <div className='list-div'>
            <div className='list-sku'>{item?.code}</div>
            <div className='list-title'>{item?.title?.[currentLocale]}</div>
        </div>
    );
}
